import React from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import WebhookIcon from '@dashboard/icons/WebhookIcon';
import Button from '@dashboard/components/Button';
import { ClockIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import WebhooksForm from './WebhooksForm';
import { Badge } from '@dashboard/components/ui/badge';
import {
  Item,
  ItemContent,
  ItemTitle,
  ItemDescription,
  ItemAction,
} from '@dashboard/components/ui/item';
import Tooltip from '@dashboard/components/Tooltip';
import Dialog from '@dashboard/components/Dialog';
import Link from '@dashboard/components/Link';
import GoogleSheetIcon from '@dashboard/icons/GoogleSheetIcon';
import { confirm } from '@dashboard/lib/confirm';
import GoogleSheetsForm from './GoogleSheetsForm';
import SlackForm from './SlackForm';
import Features from '@dashboard/enums/features';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import ZapierIcon from '@dashboard/icons/ZapierIcon';
import SlackIcon from '@dashboard/icons/SlackIcon';
import EventsLog from '@dashboard/components/EventsLog/EventsLog';
import { integrationKey, integrationsPageKey } from '@dashboard/lib/queryKeys';
import usePage from '@dashboard/hooks/usePage';
import Divider from '@dashboard/components/Divider';
import { upperFirst } from 'lodash';

const allIntegrations = [
  {
    name: 'Google Sheets',
    type: 'googleSheets',
    description: 'Send data to a Google Sheet',
    addTitle: 'Connect to Google Sheets',
    editTitle: 'Edit Google Sheets integration',
    learnMoreLink: '/docs/articles/google-sheets-integration',
    icon: GoogleSheetIcon,
    enum: Features.GOOGLE_SHEETS,
  },
  {
    name: 'Zapier',
    type: 'zapier',
    description: 'Connect with thousands of apps',
    learnMoreLink: '/docs/articles/integrate-with-zapier',
    icon: ZapierIcon,
    addIntegrationLink: 'https://zapier.com/apps/checkout-page/integrations',
    enum: Features.ZAPIER,
  },
  {
    name: 'Slack',
    type: 'slack',
    description: 'Get notified about new orders in Slack',
    addTitle: 'Connect to Slack',
    editTitle: 'Edit Slack integration',
    learnMoreLink: '/docs/articles/slack',
    icon: SlackIcon,
    enum: Features.SLACK,
  },
  {
    name: 'Page conversion webhook',
    type: 'webhook',
    description:
      'Send conversion events to your own server or third-party services from this page only',
    addTitle: 'Add a webhook URL',
    editTitle: 'Edit webhook URL',
    learnMoreLink: '/docs/articles/webhooks',
    icon: WebhookIcon,
    enum: Features.WEBHOOKS,
  },
  {
    name: 'Store webhooks',
    type: 'webhook',
    description:
      'Send events from across your entire store (payments, subscriptions, bookings, customers and more)',
    buttonTitle: 'View settings',
    buttonLink: '/dashboard/settings/webhooks',
    learnMoreLink: '/docs',
    icon: WebhookIcon,
    enum: Features.WEBHOOKS,
  },
];

export default function IntegrationsForm() {
  const { seller } = useAuth();
  const { page } = usePage();
  const { token } = useAuth();

  const { data: integrations } = useQuery({
    queryKey: integrationsPageKey(page._id),
    queryFn: async () => fetcher(`/pages/${page._id}/integrations`, token),
    enabled: !!page._id,
  });

  const availableIntegrations = allIntegrations.filter(
    integration =>
      (integration.type === 'slack' && seller.labs?.slack) || integration.type !== 'slack',
  );

  return (
    <div>
      {integrations?.length > 0 && (
        <>
          <div className="mt-4">
            <div className="text-sm font-semibold text-gray-900 dark:text-foreground">
              Active integrations
            </div>

            <div>
              {integrations?.map(integration => (
                <IntegrationListItem key={integration._id} integration={integration} page={page} />
              ))}
            </div>
          </div>

          <Divider />
        </>
      )}

      <div className="mt-4 mb-4">
        <div className="text-sm font-semibold text-gray-900 dark:text-foreground">
          Available integrations
        </div>
        <div className="mt-2 divide-y divide-gray-100 dark:divide-border">
          {availableIntegrations?.map(integration => (
            <CanUseFeature key={integration.name} feature={integration.enum} inset>
              <Item className="items-start gap-x-3 rounded-none px-0 py-4">
                {integration.icon && (
                  <integration.icon className="mt-0.5 h-6 w-6 shrink-0 dark:box-content dark:rounded-md dark:bg-white dark:p-1" />
                )}
                <ItemContent className="gap-1">
                  <ItemTitle className="font-semibold text-gray-900 dark:text-foreground">
                    <span>{integration.name}</span>

                    {integration.beta && (
                      <Badge variant="success" className="ml-2">
                        beta
                      </Badge>
                    )}
                  </ItemTitle>
                  <ItemDescription className="whitespace-normal text-sm text-gray-500 dark:text-muted-foreground">
                    {integration.description}.{' '}
                    <Link href={integration.learnMoreLink} target="_blank">
                      Learn more
                    </Link>
                  </ItemDescription>
                </ItemContent>

                <ItemAction className="self-center">
                  {integration.addIntegrationLink ? (
                    <Button
                      appearance="secondary"
                      href={integration.addIntegrationLink}
                      target="_blank"
                    >
                      Add integration
                    </Button>
                  ) : integration.buttonTitle && integration.buttonLink ? (
                    <Button target="_blank" appearance="secondary" href={integration.buttonLink}>
                      {integration.buttonTitle}
                    </Button>
                  ) : (
                    <Dialog
                      title={`Add ${integration.name} integration`}
                      description={
                        <>
                          <div>
                            {integration.description}.{' '}
                            <Link href={integration.learnMoreLink} target="_blank">
                              Learn more
                            </Link>
                          </div>
                        </>
                      }
                      cancelButton={null}
                      confirmButton={null}
                      trigger={<Button appearance="secondary">Add integration</Button>}
                      size="2xl"
                    >
                      {({ close }) => (
                        <EditIntegration
                          integration={integration}
                          page={page}
                          onDone={() => {
                            toast({
                              title: 'Integration added',
                            });
                            close();
                          }}
                        />
                      )}
                    </Dialog>
                  )}
                </ItemAction>
              </Item>
            </CanUseFeature>
          ))}
        </div>
      </div>
    </div>
  );
}

function getIntegrationLabel(integration, page) {
  switch (integration.type) {
    case 'webhook':
      return integration.config.url;
    case 'googleSheets':
      return (
        <span>
          Sheet connected to {upperFirst(page.type)} page.{' '}
          <Link
            href={`https://docs.google.com/spreadsheets/d/${integration.config.spreadsheetId}`}
            target="_blank"
          >
            Open sheet
          </Link>
        </span>
      );
    case 'slack':
      return `Notifications sent to ${integration.config.channelName?.startsWith('#') ? integration.config.channelName : `#${integration.config.channelName}`}`;
  }
}

function IntegrationListItem({ integration, page }) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const { data: integrationData, isLoading } = useQuery({
    queryKey: integrationKey(integration._id),
    queryFn: async () => {
      return fetcher(`/pages/${integration.pageId}/integrations/${integration._id}`, token);
    },
    enabled: !!integration._id,
  });

  const { mutateAsync: deleteIntegration } = useMutation({
    mutationFn: async () => {
      return fetcher(`/pages/${integration.pageId}/integrations/${integration._id}`, token, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationsPageKey(page._id) });
      queryClient.invalidateQueries({ queryKey: integrationKey(integration._id) });
    },
  });

  if (isLoading) {
    return null;
  }

  const integrationInfo = allIntegrations.find(
    integration => integration.type === integrationData.type,
  );

  const name = integrationInfo?.name;
  const label = getIntegrationLabel(integration, page);

  return (
    <Item className="items-start gap-x-3 rounded-none border-b border-gray-100 dark:border-border px-0 py-4 last:border-b-0">
      {integrationInfo?.icon && (
        <integrationInfo.icon className="mt-0.5 h-6 w-6 shrink-0 dark:box-content dark:rounded-md dark:bg-white dark:p-1" />
      )}

      <ItemContent className="gap-1">
        <ItemTitle className="font-semibold text-gray-900 dark:text-foreground">{name}</ItemTitle>
        <ItemDescription className="whitespace-normal break-all text-sm text-gray-500 dark:text-muted-foreground">
          {label}
        </ItemDescription>
      </ItemContent>

      <ItemAction className="gap-x-1 self-center">
        <Tooltip label="Events log" position="bottom">
          <Dialog
            title={`${integrationInfo.name} events log`}
            cancelButton={null}
            confirmButton={null}
            trigger={
              <Button appearance="icon-button">
                <ClockIcon className="w-4 h-4" />
              </Button>
            }
            size="5xl"
          >
            <EventsLog integrationId={integration._id} />
          </Dialog>
        </Tooltip>

        <Tooltip label="Edit" position="bottom">
          <Dialog
            title={integrationInfo.editTitle}
            description={
              <>
                <div>
                  {integrationInfo.description}.{' '}
                  <Link href={integrationInfo.learnMoreLink} target="_blank">
                    Learn more
                  </Link>
                </div>
              </>
            }
            cancelButton={null}
            confirmButton={null}
            trigger={
              <Button appearance="icon-button">
                <PencilIcon className="w-4 h-4" />
              </Button>
            }
            size="2xl"
          >
            {({ close }) => (
              <EditIntegration
                integration={integration}
                page={page}
                onDone={() => {
                  toast({
                    title: 'Integration updated',
                  });
                  close();
                }}
              />
            )}
          </Dialog>
        </Tooltip>

        <Tooltip label="Delete" position="bottom">
          <Button
            appearance="icon-button"
            onClick={async () => {
              const del = await confirm('Delete integration?', {
                description: 'This will stop sending updates through this integration.',
              });

              if (del) {
                await deleteIntegration();
              }
            }}
          >
            <TrashIcon className="w-4 h-4" />
          </Button>
        </Tooltip>
      </ItemAction>
    </Item>
  );
}

function EditIntegration({ integration, page, onDone }) {
  const { token, sellerId } = useAuth();
  const queryClient = useQueryClient();

  const createIntegrationMutation = useMutation({
    mutationFn: async ({ values }) =>
      fetcher(`/pages/${page._id}/integrations`, token, {
        method: 'POST',
        body: JSON.stringify({
          ...values,
          type: integration.type,
          sellerId: sellerId,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationsPageKey(page._id) });
      queryClient.invalidateQueries({ queryKey: integrationKey(integration._id) });
      onDone();
    },
  });

  const updateIntegrationMutation = useMutation({
    mutationFn: async ({ integrationId, values }) =>
      fetcher(`/pages/${page._id}/integrations/${integrationId}`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationsPageKey(page._id) });
      queryClient.invalidateQueries({ queryKey: integrationKey(integration._id) });
      onDone();
    },
  });

  async function onSubmit(values) {
    try {
      let data;

      if (integration._id) {
        data = await updateIntegrationMutation.mutateAsync({
          integrationId: integration._id,
          values,
        });
      } else {
        data = await createIntegrationMutation.mutateAsync({ values });
      }

      return {
        data,
      };
    } catch (err) {
      return {
        error: err,
      };
    }
  }

  switch (integration.type) {
    case 'webhook':
      return <WebhooksForm integration={integration} onSubmit={onSubmit} />;
    case 'googleSheets':
      return <GoogleSheetsForm integration={integration} onSubmit={onSubmit} />;
    case 'slack':
      return <SlackForm integration={integration} onSubmit={onSubmit} />;
    default:
      return null;
  }
}
